/**
 * repo.files.ts —— 文件仓储 + 文件摘要索引
 *
 * 文件两种来源：
 *   ① 用户通过「文件导入」选进来的（真实文件）
 *   ② 拍照 / 录音产生的 Blob
 * 两者都是真实数据，存进 IndexedDB 的 files 表。
 *
 * 设计文档 5.1 的「整合消息记录中的文件」也在这里实现：
 *   ① 收集（确定性，不涉及 AI）→ ② 落 file_digests 一条记录 → ③ AI 填 summary
 * 第二步的落库由本模块负责，第三步由调用方（file.summarize API）在拿到记录后补上。
 */

import {
  STORE,
  dbGet,
  dbGetAll,
  dbPut,
  dbDelete,
  dbQueryByIndex,
} from './db';
import { eventBus } from '@/core/event-bus';
import { uid } from './db';
import { messagesRepo } from './repo.messages';
import type { StoredFile, FileDigest, FileDigestItem, Message } from '@/types/models';

/** 把 mime 归类，用于摘要里的 byType 统计与图标选择 */
export function categorize(mime: string): string {
  if (mime.startsWith('image/')) return 'image';
  if (mime.startsWith('audio/')) return 'voice';
  if (mime.startsWith('video/')) return 'video';
  if (mime.includes('pdf')) return 'pdf';
  if (mime.startsWith('text/')) return 'doc';
  if (mime.includes('word') || mime.includes('document')) return 'doc';
  if (mime.includes('sheet') || mime.includes('excel')) return 'sheet';
  if (mime.includes('zip') || mime.includes('compressed')) return 'archive';
  return 'other';
}

/** 人类可读的体积 */
export function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

export const filesRepo = {
  async get(id: string): Promise<StoredFile | undefined> {
    return dbGet<StoredFile>(STORE.files, id);
  },

  async listBySession(sessionId: string): Promise<StoredFile[]> {
    const list = await dbQueryByIndex<StoredFile>(STORE.files, 'sessionId', sessionId);
    return list;
  },

  async listAll(): Promise<StoredFile[]> {
    return dbGetAll<StoredFile>(STORE.files);
  },

  async put(file: StoredFile): Promise<void> {
    await dbPut(STORE.files, file);
  },

  async remove(id: string): Promise<void> {
    await dbDelete(STORE.files, id);
  },

  /**
   * 发一个附件到某会话 —— **messages 与 files 两张表一起写**。
   *
   * 为什么抽到 repo 层而不是让各调用方自己写两条：
   *   这是一次**跨表写入**，两条记录通过 fileId 互相引用。
   *   如果 AI 路径（file.sendPhoto）和 UI 路径（聊天页加号）各自实现一遍，
   *   迟早出现「一条写了、另一条忘了」的坏数据 —— 而且这种坏数据
   *   在 UI 上表现正常（消息出现了），只在 file.summarize() 时才暴露。
   *
   * 顺序是有意的：先 append 消息（它会顺带 touch 会话预览并广播），
   * 再写 file 记录。这样即使 file 写入失败，用户至少看到消息发出去了，
   * 而不是「什么都没发生」。
   */
  async attachToMessage(input: {
    sessionId: string;
    /** 'me' 或 contactId */
    sender: string;
    name: string;
    blob: Blob;
    /** 归类用的 mime，如 image/png、application/pdf */
    mime: string;
    /** 消息类型，与 MessageType 一致 */
    kind: 'image' | 'file' | 'voice';
  }): Promise<{ messageId: string; fileId: string }> {
    const fileId = uid();

    const message = await messagesRepo.append({
      sessionId: input.sessionId,
      sender: input.sender,
      type: input.kind,
      text: input.name,
      fileId,
    });

    await dbPut(STORE.files, {
      id: fileId,
      messageId: message.id,
      sessionId: input.sessionId,
      name: input.name,
      size: input.blob.size,
      mime: input.mime,
      blob: input.blob,
    } satisfies StoredFile);

    return { messageId: message.id, fileId };
  },

  /**
   * 收集某会话的全部文件条目。
   * 纯本地确定性操作 —— 不涉及 AI，可重复执行结果一致。
   */
  async collectInSession(sessionId: string): Promise<FileDigestItem[]> {
    const [files, messages] = await Promise.all([
      this.listBySession(sessionId),
      dbQueryByIndex<Message>(STORE.messages, 'sessionId', sessionId),
    ]);
    const msgById = new Map(messages.map((m) => [m.id, m]));

    return files
      .map((f) => {
        const msg = msgById.get(f.messageId);
        return {
          fileId: f.id,
          messageId: f.messageId,
          name: f.name,
          size: f.size,
          mime: f.mime,
          // 时间取消息时间，比文件记录本身更准确地反映"什么时候发来的"
          createdAt: msg?.createdAt ?? 0,
        };
      })
      .sort((a, b) => a.createdAt - b.createdAt);
  },

  /**
   * 生成（或更新）一份文件摘要索引记录。
   * 注意 summary 此时为空 —— 它由 AI 在之后填入。
   * 这样设计是为了让「收集 + 落库」这一步不依赖 AI，AI 挂了数据也在。
   */
  async createDigest(sessionId: string, title: string, items: FileDigestItem[]): Promise<FileDigest> {
    const byType: Record<string, number> = {};
    let totalSize = 0;
    for (const it of items) {
      const cat = categorize(it.mime);
      byType[cat] = (byType[cat] ?? 0) + 1;
      totalSize += it.size;
    }

    const digest: FileDigest = {
      id: uid(),
      sessionId,
      title,
      itemCount: items.length,
      totalSize,
      byType,
      items,
      summary: '',
      createdAt: Date.now(),
    };

    await dbPut(STORE.fileDigests, digest);
    eventBus.emit('digest:created', { digestId: digest.id, sessionId });
    return digest;
  },

  /** 填入 AI 生成的摘要文字 */
  async setDigestSummary(digestId: string, summary: string): Promise<void> {
    const d = await dbGet<FileDigest>(STORE.fileDigests, digestId);
    if (!d) return;
    d.summary = summary;
    await dbPut(STORE.fileDigests, d);
  },

  async getDigest(id: string): Promise<FileDigest | undefined> {
    return dbGet<FileDigest>(STORE.fileDigests, id);
  },

  async listDigests(sessionId?: string): Promise<FileDigest[]> {
    const all = sessionId
      ? await dbQueryByIndex<FileDigest>(STORE.fileDigests, 'sessionId', sessionId)
      : await dbGetAll<FileDigest>(STORE.fileDigests);
    return all.sort((a, b) => b.createdAt - a.createdAt);
  },

  async removeDigest(id: string): Promise<void> {
    const d = await dbGet<FileDigest>(STORE.fileDigests, id);
    if (!d) return;
    await dbDelete(STORE.fileDigests, id);
    eventBus.emit('digest:removed', { digestId: id });
  },
};
